import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import SearchComponent from '../../components/ui/SearchComponent';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import GrantCard from './components/GrantCard';
import GrantDetailModal from './components/GrantDetailModal';
import FilterPanel from './components/FilterPanel';
import AddGrantModal from './components/AddGrantModal';
import { grantService } from '../../utils/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const GrantOpportunitiesPortal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [grants, setGrants] = useState([]);
  const [filteredGrants, setFilteredGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    focus_area: '',
    amount_min: '',
    sort: 'deadline'
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [error, setError] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/city-official-dashboard' },
    { label: 'Grant Opportunities' }
  ];

  // Focus areas from schema enum
  const focusAreas = [
    'Urban Mobility',
    'Sustainability',
    'Digital Infrastructure',
    'Data Analytics',
    'IoT Solutions',
    'AI & Machine Learning',
    'GIS & Mapping',
    'Energy Management',
    'Water Systems',
    'Waste Management',
    'Public Safety',
    'Economic Development'
  ];

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'closing_soon', label: 'Closing Soon' },
    { value: 'closed', label: 'Closed' },
    { value: 'review', label: 'Under Review' },
    { value: 'awarded', label: 'Awarded' }
  ];

  const sortOptions = [
    { value: 'deadline', label: 'Deadline (Earliest)' },
    { value: 'amount_desc', label: 'Amount (Highest)' },
    { value: 'amount_asc', label: 'Amount (Lowest)' },
    { value: 'created_desc', label: 'Recently Added' },
    { value: 'title', label: 'Title (A-Z)' }
  ];

  useEffect(() => {
    fetchGrants();
  }, []);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [grants, searchQuery, filters]);

  const fetchGrants = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: grantsError } = await grantService?.getAllGrants();
      
      if (grantsError) {
        setError('Failed to load grant opportunities');
        return;
      }
      
      setGrants(data || []);
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...grants];

    // Apply search
    if (searchQuery) {
      filtered = filtered?.filter(grant =>
        grant?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        grant?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        grant?.provider_organization?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply filters
    if (filters?.status) {
      filtered = filtered?.filter(grant => grant?.status === filters?.status);
    }

    if (filters?.focus_area) {
      filtered = filtered?.filter(grant => 
        grant?.focus_areas?.includes(filters?.focus_area)
      );
    }

    if (filters?.amount_min) {
      const minAmount = parseFloat(filters?.amount_min);
      filtered = filtered?.filter(grant => 
        grant?.amount_max && parseFloat(grant?.amount_max) >= minAmount
      );
    }

    // Apply sorting
    switch (filters?.sort) {
      case 'deadline':
        filtered?.sort((a, b) => new Date(a?.deadline) - new Date(b?.deadline));
        break;
      case 'amount_desc':
        filtered?.sort((a, b) => (b?.amount_max || 0) - (a?.amount_max || 0));
        break;
      case 'amount_asc':
        filtered?.sort((a, b) => (a?.amount_max || 0) - (b?.amount_max || 0));
        break;
      case 'created_desc':
        filtered?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
        break;
      case 'title':
        filtered?.sort((a, b) => (a?.title || '')?.localeCompare(b?.title || ''));
        break;
      default:
        break;
    }

    setFilteredGrants(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      focus_area: '',
      amount_min: '',
      sort: 'deadline'
    });
    setSearchQuery('');
  };

  const handleGrantClick = (grant) => {
    setSelectedGrant(grant);
    setShowDetailModal(true);
  };

  const handleAddGrant = () => {
    if (!user) {
      navigate('/user-login');
      return;
    }
    setShowAddModal(true);
  };

  const onGrantAdded = (newGrant) => {
    setGrants(prev => [newGrant, ...prev]);
    setShowAddModal(false);
  };

  const canAddGrant = user && (user?.role === 'pcf_admin' || user?.role === 'city_official');

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 lg:px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading grant opportunities...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 lg:px-6">
        <div className="mb-6">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Grant Opportunities Portal</h1>
              <p className="text-muted-foreground mt-1">
                Discover funding opportunities for smart city initiatives and innovative projects
              </p>
            </div>
            
            {canAddGrant && (
              <Button
                onClick={handleAddGrant}
                iconName="Plus"
                iconPosition="left"
              >
                Add New Grant
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-4">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
              focusAreas={focusAreas}
              statusOptions={statusOptions}
              sortOptions={sortOptions}
            />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Search and View Controls */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1 max-w-md">
                  <SearchComponent
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search grants by title, description, or organization..."
                    className="w-full"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Select
                    options={sortOptions}
                    value={filters?.sort}
                    onChange={(value) => handleFilterChange('sort', value)}
                    placeholder="Sort by"
                    className="w-40"
                  />
                  
                  <div className="flex border border-border rounded-lg">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className="rounded-r-none border-r"
                    >
                      <Icon name="Grid3x3" size={16} />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className="rounded-l-none"
                    >
                      <Icon name="List" size={16} />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredGrants?.length} of {grants?.length} grant opportunities
                </p>
                
                {(searchQuery || Object.values(filters)?.some(v => v && v !== 'deadline')) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                  >
                    <Icon name="X" size={14} className="mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            {/* Grants Grid/List */}
            {filteredGrants?.length > 0 ? (
              <div className={viewMode === 'grid' 
                ? "grid gap-6 md:grid-cols-2 xl:grid-cols-3" :"space-y-4"
              }>
                {filteredGrants?.map((grant) => (
                  <GrantCard
                    key={grant?.id}
                    grant={grant}
                    onClick={() => handleGrantClick(grant)}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No grants found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || Object.values(filters)?.some(v => v && v !== 'deadline')
                    ? "Try adjusting your search or filters to find more grants." :"There are no grant opportunities available at the moment."
                  }
                </p>
                {(searchQuery || Object.values(filters)?.some(v => v && v !== 'deadline')) && (
                  <Button variant="outline" onClick={clearFilters}>
                    <Icon name="RefreshCw" size={14} className="mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Grant Detail Modal */}
        {showDetailModal && selectedGrant && (
          <GrantDetailModal
            grant={selectedGrant}
            onClose={() => setShowDetailModal(false)}
          />
        )}

        {/* Add Grant Modal */}
        {showAddModal && (
          <AddGrantModal
            onClose={() => setShowAddModal(false)}
            onGrantAdded={onGrantAdded}
            focusAreas={focusAreas}
          />
        )}
      </main>
    </div>
  );
};

export default GrantOpportunitiesPortal;